import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { BaseFormModal } from '@/components/BaseFormModal';
import { Dropdownlist } from '@/components/Dropdownlist';
import { focusAfterPaint } from '@/utils/dom';
import { NOC_PRIORITY_OPTIONS } from '@/constants/priorities';

export interface NocGroupOption {
  code: number;
  value: string;
}

export interface AddCustomNocModalProps {
  open: boolean;
  groupList?: NocGroupOption[];
  subgroupList?: NocGroupOption[];
  theme?: string;
  onSave: (data: { value: string; groupCode: number; subgroupCode: number; priority: number | null }) => void;
  onCancel: () => void;
}

const PRIORITY_ITEMS = NOC_PRIORITY_OPTIONS.map((opt) => ({
  label: opt.label,
  value: opt.value === null ? '__none__' : opt.value,
}));

export function AddCustomNocModal({
  open,
  groupList = [],
  subgroupList = [],
  theme,
  onSave,
  onCancel,
}: AddCustomNocModalProps) {
  const [nocValue, setNocValue] = useState('');
  const [groupCode, setGroupCode] = useState<number | null>(null);
  const [subgroupCode, setSubgroupCode] = useState<number | null>(null);
  const [priority, setPriority] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset form and focus input when modal opens
  useEffect(() => {
    if (open) {
      setNocValue('');
      setGroupCode(null);
      setSubgroupCode(null);
      setPriority(null);

      focusAfterPaint(inputRef);
    }
  }, [open]);

  const groupItems = useMemo(
    () =>
      groupList.map((g) => ({
        label: g.value,
        value: g.code,
      })),
    [groupList]
  );

  const subgroupItems = useMemo(
    () =>
      subgroupList.map((s) => ({
        label: s.value,
        value: s.code,
      })),
    [subgroupList]
  );

  const handleSave = useCallback(() => {
    if (!nocValue || !nocValue.trim()) {
      (window as any).App?.showPopupNotification?.('Nature of call required.', 'error');
      return;
    }
    if (groupCode === null) {
      (window as any).App?.showPopupNotification?.('Group required.', 'error');
      return;
    }
    if (subgroupCode === null) {
      (window as any).App?.showPopupNotification?.('Subgroup required.', 'error');
      return;
    }
    if (nocValue.includes('[[') || nocValue.includes(']]')) {
      (window as any).App?.showPopupNotification?.('Custom NOC cannot include "[[" or "]]".', 'error');
      return;
    }

    onSave({
      value: nocValue.trim(),
      groupCode,
      subgroupCode,
      priority,
    });
  }, [nocValue, groupCode, subgroupCode, priority, onSave]);

  const priorityValue = priority === null ? '__none__' : priority;
  const handlePriorityChange = useCallback((val: string | number | null) => {
    if (val === '__none__' || val === null) {
      setPriority(null);
    } else {
      setPriority(Number(val));
    }
  }, []);

  return (
    <BaseFormModal
      open={open}
      title="Add Custom Nature of Call"
      theme={theme}
      dialogClass="kstc-dialog--form"
      hint="If you need a custom group or subgroup, please contact support@kerkton.com."
      saveText="Add"
      onSave={handleSave}
      onCancel={onCancel}
    >
      <div className="kstc-form-group">
        <label className="kstc-label">Nature of Call</label>
        <input
          ref={inputRef}
          type="text"
          className="kstc-input kst-noc-input"
          value={nocValue}
          onChange={(e) => setNocValue(e.target.value)}
          placeholder="Enter nature of call..."
        />
      </div>

      <div className="kstc-form-group">
        <label className="kstc-label">Group</label>
        <Dropdownlist
          value={groupCode}
          items={groupItems}
          placeholder="Select group..."
          sortItems={true}
          searchable={false}
          onChange={(val) => setGroupCode(val as number | null)}
        />
      </div>

      <div className="kstc-form-group">
        <label className="kstc-label">Subgroup</label>
        <Dropdownlist
          value={subgroupCode}
          items={subgroupItems}
          placeholder="Select subgroup..."
          sortItems={true}
          onChange={(val) => setSubgroupCode(val as number | null)}
        />
      </div>

      <div className="kstc-form-group">
        <label className="kstc-label">Priority (optional)</label>
        <Dropdownlist
          value={priorityValue as any}
          items={PRIORITY_ITEMS}
          placeholder="None"
          sortItems={false}
          searchable={false}
          onChange={handlePriorityChange}
        />
        <p className="kstc-hint">Higher-priority NOCs can raise the Call Card priority when attached.</p>
      </div>
    </BaseFormModal>
  );
}

(window as any).AddCustomNocModalReact = AddCustomNocModal;
